/**
 * Constants for the CascadeSettingsModal module.
 * Includes setting labels, setting group definitions, default state factories,
 * CICO parent-child relationships, and numeric layout constants.
 */
import { CascadeSettingKey, CascadeSettings, SettingGroupDef } from "./types"

/** Modal Width */
export const MODAL_WIDTH = "840px"

/** Maximum time (ms) to poll before giving up and showing an error. */
export const TASK_POLL_TIMEOUT_MS = 5 * 60 * 1000 // hopefully things aren't taking 5+ minutes

/** Row height (px) for all rows in the virtualized entity list in Step 2. */
export const ENTITY_ROW_HEIGHT = 50

/** Column width (px) for checkbox columns in the subgroup tree. */
export const TREE_CHECKBOX_COL_WIDTH = 100

/** Indentation size (px) per depth level in the subgroup tree. */
export const TREE_INDENT_PX = 8

/** Returns a fresh default CascadeSettings object with all settings disabled. */
export const defaultSettings = (): CascadeSettings => ({
    logo: false,
    cohorts: false,
    rate_tables: false,
    work_rule_sets: false,
    attestation_language: false,
    grants_employee_access: false,
    grants_project_access: false,
    pto_buckets: false,
    cico_enabled: false,
    cico_rounding: false,
    cico_geofence: false,
    cico_breaks: false,
    cico_meals: false,
    cico_location: false,
    cico_location_employee_overrides: false,
    cico_location_cohort_overrides: false,
    cico_photo: false,
    cico_photo_employee_overrides: false,
    cico_photo_cohort_overrides: false,
})

/** Human-readable labels for each setting key. */
export const SETTING_LABELS: Record<CascadeSettingKey, string> = {
    logo: "Logo",
    cohorts: "Assigned Cohorts",
    rate_tables: "Rate Tables",
    work_rule_sets: "Work Rule Sets",
    attestation_language: "Signature Attestation Language",
    grants_employee_access: "Grant Employee Access",
    grants_project_access: "Grant Project Access",
    pto_buckets: "PTO Buckets",
    cico_enabled: "On/Off",
    cico_rounding: "Rounding",
    cico_geofence: "Geofence",
    cico_breaks: "Breaks",
    cico_meals: "Meals",
    cico_location: "Location Capture",
    cico_location_employee_overrides: "Location Employee Overrides",
    cico_location_cohort_overrides: "Location Cohort Overrides",
    cico_photo: "Photo Capture",
    cico_photo_employee_overrides: "Photo Employee Overrides",
    cico_photo_cohort_overrides: "Photo Cohort Overrides",
}

/**
 * Setting group definitions for the Step 2 preview. Groups with a single setting key
 * are a single nav item in the left pane. Groups with multiple setting keys (e.g. CICO)
 * render as a non-clickable section label followed by one nav item per setting key.
 */
export const SETTING_GROUPS: SettingGroupDef[] = [
    { groupKey: "logo", label: "Logo", settingKeys: ["logo"] },
    {
        groupKey: "attestation_language",
        label: "Signature Attestation Language",
        settingKeys: ["attestation_language"],
    },
    { groupKey: "cohorts", label: "Assigned Cohorts", settingKeys: ["cohorts"] },
    { groupKey: "rate_tables", label: "Rate Tables", settingKeys: ["rate_tables"] },
    { groupKey: "work_rule_sets", label: "Work Rule Sets", settingKeys: ["work_rule_sets"] },
    { groupKey: "pto_buckets", label: "PTO Buckets", settingKeys: ["pto_buckets"] },
    {
        groupKey: "grants_employee_access",
        label: "Grant Employee Access (Subgroups Only)",
        settingKeys: ["grants_employee_access"],
    },
    {
        groupKey: "grants_project_access",
        label: "Grant Project Access (Subgroups Only)",
        settingKeys: ["grants_project_access"],
    },
    {
        groupKey: "cico",
        label: "Clock In / Clock Out (CICO)",
        breadcrumbLabel: "CICO",
        settingKeys: [
            "cico_enabled",
            "cico_rounding",
            "cico_geofence",
            "cico_breaks",
            "cico_meals",
            "cico_location",
            "cico_location_employee_overrides",
            "cico_location_cohort_overrides",
            "cico_photo",
            "cico_photo_employee_overrides",
            "cico_photo_cohort_overrides",
        ],
    },
]

/**
 * CICO parent → child setting key relationships used for cascading exclusions.
 * When a parent is excluded for an entity, its children are also excluded.
 */
export const CICO_PARENT_CHILDREN: Partial<Record<CascadeSettingKey, CascadeSettingKey[]>> = {
    cico_location: ["cico_location_employee_overrides", "cico_location_cohort_overrides"],
    cico_photo: ["cico_photo_employee_overrides", "cico_photo_cohort_overrides"],
}
